import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Search, OpenInNew, FilterList } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  materialTakeoffService,
  jobStatusColor,
  jobStatusLabel,
  takeoffStatusColor,
  takeoffStatusLabel,
} from '../services/materialTakeoffService';
import usePersistedListFilters from '../hooks/usePersistedListFilters';

const LIST_FILTERS_DEFAULTS = { search: '' };

export default function MaterialTakeoffs() {
  const navigate = useNavigate();
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_material_takeoffs_list_filters', LIST_FILTERS_DEFAULTS);
  const search = listFilters.search;
  const setSearch = useCallback((v) => setListFilter('search', v), [setListFilter]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await materialTakeoffService.list({ search: search.trim() || undefined });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load material takeoffs');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Material takeoffs</Typography>
        <Typography variant="body2" color="text.secondary">
          AI-assisted buy lists from customer drawing packages
        </Typography>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search title, quote, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          {hasActiveFilters ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={clearListFilters}
            >
              Clear filters
            </Button>
          ) : null}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Quote</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Analysis</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No material takeoffs yet. Create one from a quote&apos;s attachments tab.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/material-takeoffs/${row.id}`)}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.quote_number || '—'}</TableCell>
                    <TableCell>{row.customer_name || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={takeoffStatusLabel(row.status)} color={takeoffStatusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {row.latest_job_status ? (
                        <Chip size="small" label={jobStatusLabel(row.latest_job_status)} color={jobStatusColor(row.latest_job_status)} variant="outlined" />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.updated_at ? format(new Date(row.updated_at), 'MM/dd/yyyy HH:mm') : '—'}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Button size="small" startIcon={<OpenInNew />} onClick={() => navigate(`/material-takeoffs/${row.id}`)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
